/**
 * Type definitions for generic contract form system
 *
 * This module provides all TypeScript interfaces and types needed
 * for the configuration-based contract form architecture.
 *
 * @module contractForm.types
 * @author AI Lawyer System
 * @version 1.0.0
 */

/**
 * Supported field types for contract forms
 *
 * @enum {string}
 * @example
 * ```typescript
 * const fieldConfig: FieldConfig = {
 *   name: 'email',
 *   label: 'Email Address',
 *   type: FieldType.EMAIL
 * };
 * ```
 */
export enum FieldType {
  /** Standard text input */
  TEXT = 'text',
  /** Email input with validation */
  EMAIL = 'email',
  /** Telephone number input */
  TEL = 'tel',
  /** Numeric input */
  NUMBER = 'number',
  /** Date picker */
  DATE = 'date',
  /** Single select dropdown */
  SELECT = 'select',
  /** Multi-select dropdown */
  MULTISELECT = 'multiselect',
  /** Multi-line text area */
  TEXTAREA = 'textarea',
  /** Checkbox input */
  CHECKBOX = 'checkbox',
  /** Radio button group */
  RADIO = 'radio',
  /** File upload */
  FILE = 'file',
  /** Currency input with formatting */
  CURRENCY = 'currency',
  /** Percentage input */
  PERCENTAGE = 'percentage',
  /** Vehicle Identification Number (custom validation) */
  VIN = 'vin',
  /** Czech registration plate (custom validation) */
  REGISTRATION_PLATE = 'registration_plate',
  /** Czech business ID (IČO) */
  ICO = 'ico',
  /** Czech tax ID (DIČ) */
  DIC = 'dic',
  /** Multi-line text (alias for TEXTAREA for backwards compatibility) */
  MULTILINE = 'multiline'
}

/**
 * Validation rule types
 *
 * @type ValidationType
 */
export type ValidationType =
  | 'required'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
  | 'custom'
  | 'min'
  | 'max'
  | 'email';

/**
 * Conditional logic operators
 *
 * @type ConditionalOperator
 */
export type ConditionalOperator =
  | 'equals'
  | 'notEquals'
  | 'includes'
  | 'notIncludes'
  | 'greaterThan'
  | 'lessThan';

/**
 * Validation rule configuration
 *
 * @interface ValidationRule
 * @example
 * ```typescript
 * const requiredRule: ValidationRule = {
 *   type: 'required',
 *   message: 'This field is required'
 * };
 *
 * const minLengthRule: ValidationRule = {
 *   type: 'minLength',
 *   value: 5,
 *   message: 'Minimum 5 characters required'
 * };
 *
 * const customRule: ValidationRule = {
 *   type: 'custom',
 *   message: 'Values must match',
 *   validator: (value, formData) => value === formData.password
 * };
 * ```
 */
export interface ValidationRule {
  /** Type of validation to perform */
  type: ValidationType;
  /** Error message to display when validation fails */
  message: string;
  /**
   * Value for validation (e.g., min/max length, pattern)
   * - For minLength/maxLength: number
   * - For min/max: number
   * - For pattern: string (regex pattern)
   */
  value?: string | number | RegExp;
  /**
   * Custom validation function
   * @param value - Current field value
   * @param formData - Complete form data for cross-field validation
   * @returns true if valid, false if invalid
   */
  validator?: (value: any, formData: FormData) => boolean | Promise<boolean>;
}

/**
 * Conditional rendering logic for fields
 *
 * Fields with conditional logic are only rendered when the condition is met.
 *
 * @interface ConditionalLogic
 * @example
 * ```typescript
 * // Show "Other vehicle type" field when vehicleType is "other"
 * const conditional: ConditionalLogic = {
 *   field: 'vehicleType',
 *   operator: 'equals',
 *   value: 'other'
 * };
 *
 * // Show discount field when purchase amount > 10000
 * const conditionalNumeric: ConditionalLogic = {
 *   field: 'purchaseAmount',
 *   operator: 'greaterThan',
 *   value: 10000
 * };
 * ```
 */
export interface ConditionalLogic {
  /** Name of the field to watch for changes */
  field: string;
  /** Comparison operator to use */
  operator: ConditionalOperator;
  /** Value to compare against */
  value: any;
  /** Show field when condition is true, hide when false (default: true) */
  showWhenTrue?: boolean;
}

/**
 * Grid layout properties for responsive field positioning
 * Based on Material-UI Grid system (12-column layout)
 *
 * @interface GridProps
 * @example
 * ```typescript
 * const gridProps: GridProps = {
 *   xs: 12,  // Full width on extra-small screens
 *   sm: 6,   // Half width on small screens
 *   md: 4,   // Third width on medium screens
 *   lg: 3    // Quarter width on large screens
 * };
 * ```
 */
export interface GridProps {
  /** Extra small screens (<600px) - columns out of 12 */
  xs?: number;
  /** Small screens (≥600px) - columns out of 12 */
  sm?: number;
  /** Medium screens (≥960px) - columns out of 12 */
  md?: number;
  /** Large screens (≥1280px) - columns out of 12 */
  lg?: number;
  /** Extra large screens (≥1920px) - columns out of 12 */
  xl?: number;
}

/**
 * Option configuration for select, radio, and multiselect fields
 *
 * @interface SelectOption
 * @example
 * ```typescript
 * const vehicleTypeOptions: SelectOption[] = [
 *   { value: 'car', label: 'Osobní automobil' },
 *   { value: 'motorcycle', label: 'Motocykl' },
 *   { value: 'truck', label: 'Nákladní vozidlo' }
 * ];
 * ```
 */
export interface SelectOption {
  /** Internal value stored in form data */
  value: string | number;
  /** Display label shown to user */
  label: string;
  /** Optional: Disable this option */
  disabled?: boolean;
  /** Optional icon/emoji to display */
  icon?: string;
}

/**
 * Main field configuration interface
 *
 * Defines all properties for a single form field including validation,
 * conditional logic, and UI presentation.
 *
 * @interface FieldConfig
 * @example
 * ```typescript
 * const emailField: FieldConfig = {
 *   name: 'email',
 *   label: 'Email Address',
 *   type: FieldType.EMAIL,
 *   validation: [
 *     { type: 'required', message: 'Email is required' },
 *     { type: 'email', message: 'Invalid email format' }
 *   ],
 *   placeholder: 'example@domain.com',
 *   gridProps: { xs: 12, md: 6 },
 *   ragEnabled: true
 * };
 *
 * const vehicleTypeField: FieldConfig = {
 *   name: 'vehicleType',
 *   label: 'Type of Vehicle',
 *   type: FieldType.SELECT,
 *   options: [
 *     { value: 'car', label: 'Car' },
 *     { value: 'motorcycle', label: 'Motorcycle' }
 *   ],
 *   validation: [{ type: 'required', message: 'Select vehicle type' }]
 * };
 * ```
 */
export interface FieldConfig {
  /** Unique field identifier (used as form data key) */
  name: string;
  /** Display label for the field */
  label: string;
  /** Field type determining input component and behavior */
  type: FieldType;
  /** Array of validation rules to apply */
  validation?: ValidationRule[];
  /** Conditional logic to control field visibility */
  conditional?: ConditionalLogic;
  /** Placeholder text for input fields */
  placeholder?: string;
  /** Helper text displayed below the field */
  helperText?: string;
  /** Help text displayed below the field (alias for helperText) */
  helpText?: string;
  /** Default value when form initializes */
  defaultValue?: any;
  /** Options for select, radio, and multiselect fields */
  options?: SelectOption[];
  /** Enable multiple selection (for select fields) */
  multiple?: boolean;
  /** Number of rows for textarea fields */
  rows?: number;
  /** Responsive grid layout configuration */
  gridProps?: GridProps;
  /** Enable RAG (Retrieval Augmented Generation) suggestions */
  ragEnabled?: boolean;
  /** Disable field interaction */
  disabled?: boolean;
  /** Make field read-only (visible but not editable) */
  readOnly?: boolean;
  /** HTML autocomplete attribute value */
  autoComplete?: string;
  /** Minimum value for number/currency/percentage fields */
  min?: number;
  /** Maximum value for number/currency/percentage fields */
  max?: number;
  /** Step increment for number fields */
  step?: number;
  /** Accept attribute for file inputs (e.g., 'image/*', '.pdf') */
  accept?: string;
  /** Maximum file size in bytes for file inputs */
  maxFileSize?: number;
  /** Currency code for currency fields (e.g., 'CZK', 'EUR') */
  currency?: string;
  /** Number of decimal places for currency/percentage fields */
  decimalPlaces?: number;
  /** Example values for user guidance */
  examples?: string[];
  /** Custom input props */
  inputProps?: Record<string, any>;
}

/**
 * Form step/section configuration
 *
 * Organizes fields into logical steps for multi-step forms.
 *
 * @interface StepConfig
 * @example
 * ```typescript
 * const personalInfoStep: StepConfig = {
 *   title: 'Personal Information',
 *   description: 'Enter your basic details',
 *   fields: [
 *     { name: 'firstName', label: 'First Name', type: FieldType.TEXT },
 *     { name: 'lastName', label: 'Last Name', type: FieldType.TEXT }
 *   ],
 *   validation: [
 *     {
 *       type: 'custom',
 *       message: 'At least one contact method required',
 *       validator: (_, formData) => !!(formData.email || formData.phone)
 *     }
 *   ]
 * };
 * ```
 */
export interface StepConfig {
  /** Step title displayed in navigation */
  title: string;
  /** Optional subtitle for additional context */
  subtitle?: string;
  /** Optional description shown at the top of the step */
  description?: string;
  /** Array of fields in this step */
  fields: FieldConfig[];
  /** Step-level validation rules (applied across multiple fields) */
  validation?: ValidationRule[];
  /** Optional icon name for step indicator */
  icon?: string;
  /** Color theme for the step */
  color?: 'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info';
}

/**
 * RAG (Retrieval Augmented Generation) configuration
 *
 * @interface RAGConfig
 * @example
 * ```typescript
 * const ragConfig: RAGConfig = {
 *   enabled: true,
 *   fields: ['description', 'terms', 'conditions'],
 *   minConfidence: 0.7
 * };
 * ```
 */
export interface RAGConfig {
  /** Enable RAG functionality */
  enabled: boolean;
  /** Array of field names that support RAG suggestions */
  fields: string[];
  /** Minimum confidence threshold for suggestions (0-1) */
  minConfidence?: number;
  /** Maximum number of suggestions to show */
  maxSuggestions?: number;
  /** Optional custom prompt template for RAG */
  promptTemplate?: string;
}

/**
 * Form-level validation configuration
 *
 * @interface FormValidationConfig
 * @example
 * ```typescript
 * const validationConfig: FormValidationConfig = {
 *   onSubmit: async (formData) => {
 *     // Validate against backend API
 *     const response = await fetch('/api/validate', {
 *       method: 'POST',
 *       body: JSON.stringify(formData)
 *     });
 *     return response.ok;
 *   },
 *   customRules: [
 *     {
 *       type: 'custom',
 *       message: 'End date must be after start date',
 *       validator: (_, data) => new Date(data.endDate) > new Date(data.startDate)
 *     }
 *   ]
 * };
 * ```
 */
export interface FormValidationConfig {
  /**
   * Custom validation function executed on form submission
   * @param formData - Complete form data
   * @returns Promise resolving to true if valid, false if invalid
   */
  onSubmit?: (formData: FormData) => Promise<boolean>;
  /** Form-level custom validation rules */
  customRules?: ValidationRule[];
}

/**
 * Main contract form configuration
 *
 * Complete configuration for a contract form including all steps,
 * fields, validation, and RAG settings.
 *
 * @interface ContractFormConfig
 * @example
 * ```typescript
 * const purchaseContractConfig: ContractFormConfig = {
 *   type: 'purchase',
 *   subtype: 'vehicle',
 *   steps: [
 *     {
 *       title: 'Vehicle Information',
 *       fields: [
 *         { name: 'vin', label: 'VIN', type: FieldType.VIN },
 *         { name: 'make', label: 'Make', type: FieldType.TEXT }
 *       ]
 *     },
 *     {
 *       title: 'Buyer Information',
 *       fields: [
 *         { name: 'buyerName', label: 'Name', type: FieldType.TEXT }
 *       ]
 *     }
 *   ],
 *   initialValues: {
 *     currency: 'CZK',
 *     country: 'CZ'
 *   },
 *   rag: {
 *     enabled: true,
 *     fields: ['description', 'additionalTerms']
 *   }
 * };
 * ```
 */
export interface ContractFormConfig {
  /** Contract type identifier (e.g., 'purchase', 'rental', 'employment') */
  type: string;
  /** Optional contract subtype (e.g., 'vehicle', 'apartment', 'full-time') */
  subtype?: string;
  /** Array of form steps/sections */
  steps: StepConfig[];
  /** Initial values for form fields */
  initialValues?: FormData;
  /** Form-level validation configuration */
  validation?: FormValidationConfig;
  /** RAG configuration for AI-powered suggestions */
  rag?: RAGConfig;
  /** Form metadata (title, description, etc.) */
  metadata?: {
    /** Form title displayed to users */
    title?: string;
    /** Form description */
    description?: string;
    /** Version number for config versioning */
    version?: string;
    /** Creation/modification timestamp */
    lastModified?: string;
  };
  /** Custom validation function for the entire form (legacy support) */
  customValidation?: (formData: FormData) => { valid: boolean; errors?: Record<string, string> };
  /** Custom transformation before submission */
  transformData?: (formData: FormData) => FormData;
}

/**
 * Generic form data type
 *
 * Represents the complete state of form data as key-value pairs.
 * Keys correspond to field names, values can be any type.
 *
 * @typedef {Record<string, any>} FormData
 * @example
 * ```typescript
 * const formData: FormData = {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com',
 *   age: 30,
 *   subscribeNewsletter: true
 * };
 * ```
 */
export type FormData = Record<string, any>;

/**
 * Form field error state
 *
 * @interface FieldError
 * @example
 * ```typescript
 * const error: FieldError = {
 *   field: 'email',
 *   message: 'Invalid email format',
 *   type: 'validation'
 * };
 * ```
 */
export interface FieldError {
  /** Name of the field with error */
  field: string;
  /** Error message to display */
  message: string;
  /** Error type/category */
  type?: 'validation' | 'required' | 'format' | 'custom';
}

/**
 * Form state interface
 *
 * @interface FormState
 * @example
 * ```typescript
 * const formState: FormState = {
 *   data: { firstName: 'John', lastName: 'Doe' },
 *   errors: [],
 *   touched: { firstName: true, lastName: false },
 *   isValid: true,
 *   isSubmitting: false,
 *   currentStep: 0
 * };
 * ```
 */
export interface FormState {
  /** Current form data values */
  data: FormData;
  /** Array of current validation errors */
  errors: FieldError[];
  /** Fields that have been interacted with */
  touched: Record<string, boolean>;
  /** Overall form validity state */
  isValid: boolean;
  /** Form submission in progress */
  isSubmitting: boolean;
  /** Current step index (for multi-step forms) */
  currentStep: number;
  /** Form has been modified from initial values */
  isDirty?: boolean;
}

/**
 * Form submission result
 *
 * @interface FormSubmissionResult
 * @example
 * ```typescript
 * const result: FormSubmissionResult = {
 *   success: true,
 *   data: { contractId: '12345' },
 *   message: 'Contract created successfully'
 * };
 * ```
 */
export interface FormSubmissionResult {
  /** Submission was successful */
  success: boolean;
  /** Response data from submission */
  data?: any;
  /** User-friendly message */
  message?: string;
  /** Validation errors if submission failed */
  errors?: FieldError[];
}

/**
 * Field change event handler type
 *
 * @callback FieldChangeHandler
 * @param fieldName - Name of the field that changed
 * @param value - New value of the field
 * @param formData - Complete current form data
 */
export type FieldChangeHandler = (
  fieldName: string,
  value: any,
  formData: FormData
) => void | Promise<void>;

/**
 * Form submission handler type
 *
 * @callback FormSubmitHandler
 * @param formData - Complete form data to submit
 * @returns Promise resolving to submission result
 */
export type FormSubmitHandler = (
  formData: FormData
) => Promise<FormSubmissionResult>;

/**
 * Field validation result
 *
 * @interface ValidationResult
 * @example
 * ```typescript
 * const result: ValidationResult = {
 *   isValid: false,
 *   errors: ['Email is required', 'Invalid format']
 * };
 * ```
 */
export interface ValidationResult {
  /** Field passed all validations */
  isValid: boolean;
  /** Array of error messages (empty if valid) */
  errors: string[];
}

/**
 * Helper type for form data based on field names
 * Extracts field names from a contract form configuration
 *
 * @type FormDataType
 * @example
 * ```typescript
 * type MyFormData = FormDataType<typeof myContractConfig>;
 * // Result: { [fieldName: string]: any }
 * ```
 */
export type FormDataType<T extends ContractFormConfig> = {
  [K in T['steps'][number]['fields'][number]['name']]: any;
};
